import * as React from "react";
import { ArrowClockwiseRegular } from "@fluentui/react-icons";
import { useRunHistoryChartStyles } from "../styles/runHistoryChart.styles";
import { DisplayTestStatus } from "../types/TestCasesTab.types";
import { STATUS_LABEL } from "../constants/statusMapping";

export type TimeRange = "1d" | "7d" | "30d";

export interface RunBarData {
    id: string;
    label: string;
    passed: number;
    failed: number;
    skipped: number;
    flaky: number;
    timestamp?: string;
}

interface Props {
    runs: RunBarData[];
    selectedRunId?: string | null;
    onSelectRun?: (runId: string) => void;
    timeRange: TimeRange;
    onTimeRangeChange: (range: TimeRange) => void;
    loadingMore?: boolean;
    refreshing?: boolean;
    onRefresh?: () => void;
}

const TIME_RANGE_LABELS: Record<TimeRange, string> = {
    "1d": "Today",
    "7d": "Last 7 days",
    "30d": "Last 30 days",
};

function formatDate(ts: string | undefined): string {
    if (!ts) return "";
    return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateTime(ts: string | undefined): string {
    if (!ts) return "";
    const d = new Date(ts);
    const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    return `${date} at ${time}`;
}

export const RunHistoryChart: React.FC<Props> = ({ runs, selectedRunId, onSelectRun, timeRange, onTimeRangeChange, loadingMore, refreshing, onRefresh }) => {
    const s = useRunHistoryChartStyles();
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [hoveredId, setHoveredId] = React.useState<string | null>(null);
    const [tooltipPos, setTooltipPos] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const handleMouseMove = React.useCallback((e: React.MouseEvent) => {
        setTooltipPos(prev => ({ x: prev.x, y: e.clientY - 12 }));
    }, []);

    const maxTotal = Math.max(...runs.map(r => r.passed + r.failed + r.skipped + r.flaky), 1);
    const chartHeight = 128;

    // Scroll to right (most recent) when runs change
    React.useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
        }
    }, [runs.length]);

    return (
        <div className={s.card}>
            <div className={s.header}>
                <span className={s.heading}>Run History</span>
                <div className={s.headerRight}>
                    {loadingMore && (
                        <span className={s.loadingMore}>
                            <span className={s.loadingDot} />
                            <span className={s.loadingDot} />
                            <span className={s.loadingDot} />
                        </span>
                    )}
                    {onRefresh && (
                        <button className={s.refreshBtn} onClick={onRefresh} disabled={refreshing} title="Refresh">
                            <ArrowClockwiseRegular className={refreshing ? s.spinning : undefined} />
                            Refresh
                        </button>
                    )}
                    <div className={s.timeRangeGroup}>
                        {(Object.keys(TIME_RANGE_LABELS) as TimeRange[]).map(k => (
                            <button
                                key={k}
                                className={timeRange === k ? s.timeRangeBtnActive : s.timeRangeBtn}
                                onClick={() => onTimeRangeChange(k)}
                            >
                                {TIME_RANGE_LABELS[k]}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className={s.headerDivider} />
            <div className={s.chartWrapper}>
                {refreshing && <div className={s.shimmerSweep}/>}
                {runs.length === 0 && !refreshing && !loadingMore ? (
                    <div className={s.emptyState}>No runs found for this time range</div>
                ) : (
                <div className={s.chartScroll} ref={scrollRef}>
                <div className={s.chartBody}>
                    {runs.length === 0 && (refreshing || loadingMore)
                        ? Array.from({ length: 12 }).map((_, i) => (
                            <div key={i} className={s.barGroup}>
                                <div className={s.skeletonBar} style={{ height: `${40 + Math.sin(i * 1.3) * 30 + 30}px` }} />
                                <span className={s.barLabelHidden}>-</span>
                            </div>
                        ))
                        : runs.length === 0
                        ? null
                        : runs.map((run) => {
                            const total = run.passed + run.failed + run.skipped + run.flaky;
                            const h = (total / maxTotal) * chartHeight;
                            const pH = (run.passed / total) * h;
                            const dH = (run.failed / total) * h;
                            const lyH = (run.flaky / total) * h;
                            const sH = h - pH - dH - lyH;
                            const isSelected = selectedRunId === run.id;
                            const isHovered = hoveredId === run.id;
                            const thisDate = formatDate(run.timestamp);
                            return (
                                <div
                                    key={run.id}
                                    className={`${s.barGroup}`}
                                    onMouseEnter={(e) => {
                                        setHoveredId(run.id);
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                        setTooltipPos({ x: rect.left + rect.width / 2, y: e.clientY - 12 });
                                    }}
                                    onMouseMove={handleMouseMove}
                                    onMouseLeave={() => setHoveredId(null)}
                                    onClick={() => onSelectRun?.(run.id)}
                                >
                                    {isHovered && (
                                        <div className={s.tooltip} style={{ left: tooltipPos.x, top: tooltipPos.y }}>
                                            <span className={s.tooltipDate}>{formatDateTime(run.timestamp) || run.label}</span>
                                            <span className={s.tooltipGreen}>{STATUS_LABEL[DisplayTestStatus.Passed]}: {run.passed}</span>
                                            <span className={s.tooltipYellow}>{STATUS_LABEL[DisplayTestStatus.Failed]}: {run.failed}</span>
                                            <span className={s.tooltipFlaky}>{STATUS_LABEL[DisplayTestStatus.Flaky]}: {run.flaky}</span>
                                            <span className={s.tooltipRed}>{STATUS_LABEL[DisplayTestStatus.Skipped]}: {run.skipped}</span>
                                        </div>
                                    )}
                                    {total === 0 ? (
                                        <div className={s.barEmpty} />
                                    ) : (
                                        <div className={`${s.barStack} ${isSelected ? s.selected : ""}`} style={{ height: `${h}px` }}>
                                            {dH > 0 && <div className={s.barFailed} style={{ height: `${dH}px` }} />}
                                            {sH > 0 && <div className={s.barSkipped} style={{ height: `${sH}px` }} />}
                                            {lyH > 0 && <div className={s.barFlaky} style={{ height: `${lyH}px` }} />}
                                            {pH > 0 && <div className={s.barPassed} style={{ height: `${pH}px` }} />}
                                        </div>
                                    )}
                                    <span className={s.barLabel}>{thisDate}</span>
                                </div>
                            );
                        })
                    }
                </div>
                </div>
                )}
            </div>
            <div className={s.legend}>
                <span className={s.legendItem}><span className={s.swatchPassed} /> {STATUS_LABEL[DisplayTestStatus.Passed]}</span>
                <span className={s.legendItem}><span className={s.swatchFailed} /> {STATUS_LABEL[DisplayTestStatus.Failed]}</span>
                <span className={s.legendItem}><span className={s.swatchFlaky} /> {STATUS_LABEL[DisplayTestStatus.Flaky]}</span>
                <span className={s.legendItem}><span className={s.swatchSkipped} /> {STATUS_LABEL[DisplayTestStatus.Skipped]}</span>
            </div>
        </div>
    );
};
